import type { PublishedContextValue } from '@/components/views/published/PublishedContext';
import { usePublishedContext } from '@/components/views/published/PublishedContext';
import type { ItemDetails } from '@/components/views/published/types';
import { usePublishedSnapshotQuery } from '@/components/views/published/usePublishedSnapshotQuery';
import { mapCustomFieldValues } from '@/lib/api/loop-visualization/mapLoopItemToItem';
import type { CustomFieldTypes } from '@/lib/api/types/customField';
import type { Item } from '@/lib/api/types/items';
import { useItemFetchQuery } from '@/lib/queries/common/useGetItemFetchQuery';
import { commonQueryKeys } from '@/lib/query-keys/common';

export function mapItemDetailsToItem(
  d: ItemDetails,
  fieldDefs: ReadonlyArray<{ id: number; type: CustomFieldTypes }> = [],
): Item {
  return {
    id: d.id,
    itemTypeId: d.itemTypeId,
    itemStatusId: d.itemStatusId ?? 0,
    name: d.name,
    description: d.description,
    parentId: d.parentId,
    schedulingType: 'PLANNED_AND_ACTUALS',
    teamId: d.teamId,
    timeSpentSeconds: d.timeSpentSeconds,
    earliestStartDateTime: d.earliestStartDateTime,
    deadlineDateTime: d.deadlineDateTime,
    plannedStartDateTime: d.plannedStartDateTime ?? null,
    plannedFinishDateTime: d.plannedFinishDateTime ?? null,
    doneDateTime: d.doneDateTime,
    externalKey: d.externalKey,
    externalLink: d.externalLink,
    loopId: 0,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    createdBy: d.createdBy,
    updatedBy: d.updatedBy,
    customFields: mapCustomFieldValues(d.customFields, fieldDefs),
    estimationId: d.estimationId,
    priority: '',
    dependencies: d.dependencies,
  };
}

function resolvePublishedItem(ctx: PublishedContextValue, itemId: number): Item | undefined {
  const itemDetails = ctx.getItemDetails(itemId);
  if (itemDetails) return mapItemDetailsToItem(itemDetails);

  const panelItemId = ctx.panelItemId;
  const panelData = panelItemId !== undefined ? ctx.getItemPanelData(panelItemId) : undefined;
  const resolved = panelData?.resolvedItems?.[String(itemId)];
  if (!resolved) return undefined;

  return {
    id: resolved.id,
    name: resolved.name,
    itemTypeId: resolved.itemTypeId,
    itemStatusId: 0,
    description: null,
    parentId: null,
    schedulingType: 'PLANNED_AND_ACTUALS',
    teamId: null,
    estimationId: null,
    timeSpentSeconds: null,
    earliestStartDateTime: null,
    deadlineDateTime: null,
    plannedStartDateTime: null,
    plannedFinishDateTime: null,
    doneDateTime: null,
    loopId: 0,
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    updatedBy: '',
    customFields: {},
    dependencies: [],
    priority: '',
  };
}

export const useItemFetchQueryWithPublished = ({
  itemId,
  enabled = true,
}: {
  itemId: number;
  enabled?: boolean;
}) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemQuery = useItemFetchQuery({
    itemId,
    enabled: !isPublished && enabled,
  });

  const publishedItemQuery = usePublishedSnapshotQuery({
    baseKey: commonQueryKeys.item(itemId),
    getSnapshot: () =>
      publishedContext && enabled && itemId > 0
        ? resolvePublishedItem(publishedContext, itemId)
        : undefined,
  });

  return isPublished ? publishedItemQuery : itemQuery;
};
